import { ColumnTypeOverride } from '@helios/filter-modal'
import { ColumnStorage, ColumnType } from '../../models/field-catalog.model'
import {
  QueryFilterType,
  QuerySortType,
} from '../../models/flex-positions-query.model'
import { ColumnCatalogEntry } from '../../models/source-definition.model'

/**
 * The one place the /query contract's TWO type vocabularies are derived.
 *
 * The contract spells the same concept differently on a filter (`filterType`: lowercase, and with a
 * `set` member that the sort side has no equivalent for) and on a sort (`type`: uppercase, with a
 * `BOOL` member the filter side has no equivalent for). They are therefore mapped through explicit
 * tables rather than by upper-casing one into the other — the two disagree on exactly the members
 * that matter.
 *
 * Sending NEITHER is the failure this file exists to prevent: an omitted type makes the backend fall
 * back to a text comparison, so a numeric column silently sorts 1000 before 9.
 */

/** Catalog {@link ColumnType} -> the filter vocabulary. */
const FILTER_TYPE_BY_COLUMN_TYPE: Record<ColumnType, QueryFilterType> = {
  TEXT: 'text',
  NUM: 'number',
  DATE: 'date',
  DATETIME: 'datetime',
  TIME: 'time',
  // The filter vocabulary has no `bool` member: a boolean column is filtered as a closed set of two
  // values (which is also how the grid presents it — see col-def-factory's `boolean` branch).
  BOOL: 'set',
}

/** Catalog {@link ColumnType} -> the sort vocabulary. */
const SORT_TYPE_BY_COLUMN_TYPE: Record<ColumnType, QuerySortType> = {
  TEXT: 'TEXT',
  NUM: 'NUM',
  DATE: 'DATE',
  DATETIME: 'DATETIME',
  TIME: 'TIME',
  BOOL: 'BOOL',
}

/** A user-chosen override -> the filter vocabulary. */
const FILTER_TYPE_BY_OVERRIDE: Record<ColumnTypeOverride, QueryFilterType> = {
  text: 'text',
  number: 'number',
  date: 'date',
}

/** A user-chosen override -> the sort vocabulary. */
const SORT_TYPE_BY_OVERRIDE: Record<ColumnTypeOverride, QuerySortType> = {
  text: 'TEXT',
  number: 'NUM',
  date: 'DATE',
}

/**
 * An unknown catalog type falls back to TEXT, which is the same thing the backend does when the type
 * is omitted — so an unrecognised value degrades to the documented default rather than to a 400.
 */
const UNKNOWN_TYPE: ColumnType = 'TEXT'

export const filterTypeOfColumnType = (
  type: ColumnType | undefined,
): QueryFilterType =>
  FILTER_TYPE_BY_COLUMN_TYPE[type ?? UNKNOWN_TYPE] ??
  FILTER_TYPE_BY_COLUMN_TYPE[UNKNOWN_TYPE]

export const sortTypeOfColumnType = (
  type: ColumnType | undefined,
): QuerySortType =>
  SORT_TYPE_BY_COLUMN_TYPE[type ?? UNKNOWN_TYPE] ??
  SORT_TYPE_BY_COLUMN_TYPE[UNKNOWN_TYPE]

/** Whether a column's type is user-owned rather than catalog-owned. */
export const isTypeOverridable = (
  storage: ColumnStorage | undefined,
): boolean => storage === 'AGG_INFO'

/**
 * Everything the request builder needs to know about one field. Deliberately a flat record rather
 * than the catalog entry itself: the builder must not be able to reach `cellType`, which collapses
 * NUM and MONEY and would silently produce the wrong `type`.
 */
export interface QueryFieldMeta {
  field: string
  filterType: QueryFilterType
  sortType: QuerySortType
  storage: ColumnStorage
}

/**
 * Resolve per-field query metadata for a source's catalog.
 *
 * `overrideFor` is consulted ONLY for AGG_INFO-storage fields. COLUMN / POS_DATA / MATCH_COUNTS
 * types are trustworthy per the contract, so an override on one of those is ignored rather than
 * honoured — that guards against a stale saved view (written when a column WAS agg-stored) retyping
 * a column that works today.
 *
 * UI-owned columns — the `calc:` unit differences, the computed Helios columns, the match-count
 * columns the UI keys by target — are absent from the catalog and therefore absent from this map,
 * which is what keeps them out of the request entirely. The backend does not know those names, so
 * filtering or sorting on one would 400.
 */
export function buildFieldMeta(
  columns: readonly ColumnCatalogEntry[],
  overrideFor: (field: string) => ColumnTypeOverride | undefined = () =>
    undefined,
): Map<string, QueryFieldMeta> {
  const meta = new Map<string, QueryFieldMeta>()
  columns.forEach(column => {
    const storage: ColumnStorage = column.storage ?? 'COLUMN'
    const override = isTypeOverridable(storage)
      ? overrideFor(column.field)
      : undefined

    meta.set(column.field, {
      field: column.field,
      filterType: override
        ? FILTER_TYPE_BY_OVERRIDE[override]
        : filterTypeOfColumnType(column.valueType),
      sortType: override
        ? SORT_TYPE_BY_OVERRIDE[override]
        : sortTypeOfColumnType(column.valueType),
      storage,
    })
  })
  return meta
}
